import { queryOptions, useQuery, useMutation, useQueryClient, useInfiniteQuery, infiniteQueryOptions } from '@tanstack/react-query';
import {
  authApi,
  userApi,
  kidsApi,
  reportApi,
  aiApi,
  communityApi,
  myApi,
} from './assets/api/apis';
import {
  SignUpDto,
  UpdateUserDto,
  CreateKidDto,
  UpdateKidDto,
  CreateFreeboardPostDto,
  CreateComplaintDto,
} from './types';

// ==========================================
// 1. 인증 관련 쿼리 & 뮤테이션 (Auth)
// ==========================================

export const authQueries = {
  all: ['auth'] as const,
};

/** 본인인증 요청 Mutation */
export function useMokStdRequestMutation() {
  return useMutation({
    mutationFn: () => authApi.mokStdRequest(),
  });
}

/** 본인인증 결과 처리 Mutation */
export function useMokStdResultMutation() {
  return useMutation({
    mutationFn: (data: any) => authApi.mokStdResult(data),
  });
}

/** 일반 로그인 Mutation */
export function useLoginMutation() {
  return useMutation({
    mutationFn: (data: SignUpDto) => authApi.login(data),
  });
}

/** 카카오 로그인 Mutation */
export function useKakaoLoginMutation() {
  return useMutation({
    mutationFn: (data: any) => authApi.kakaoLogin(data),
  });
}

/** 액세스 토큰 갱신 Mutation */
export function useUpdateTokenMutation() {
  return useMutation({
    mutationFn: (data: { refreshToken: string }) => authApi.updateToken(data),
  });
}


// ==========================================
// 2. 유저 관련 쿼리 & 뮤테이션 (User)
// ==========================================

export const userQueries = {
  all: ['user'] as const,
  // 유저 상세 정보(내 정보) 조회
  detail: () =>
    queryOptions({
      queryKey: [...userQueries.all, 'detail'] as const,
      queryFn: () => userApi.getMyInfo().then((res) => res.data),
      meta: { errorMessage: '사용자 정보를 불러오는 데 실패했습니다. 다시 시도해주세요.' },
    }),
};

/** 내 정보 조회 Query */
export function useMyInfoQuery(options?: { enabled?: boolean }) {
  return useQuery({
    ...userQueries.detail(),
    ...options,
  });
}

/** 자녀 신규 등록 Mutation */
export function useRegisterKidMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateKidDto) => userApi.registerKid(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userQueries.all });
      queryClient.invalidateQueries({ queryKey: kidsQueries.all });
    },
  });
}

/** 내 프로필 이미지 변경 Mutation */
export function useEditMyProfileImageMutation(userId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (fd: FormData) => userApi.editMyProfileImage(userId, fd),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...userQueries.all, 'detail'] });
    },
  });
}

/** 내 정보 수정 Mutation */
export function useEditMyInfoMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateUserDto) => userApi.editMyInfo(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...userQueries.all, 'detail'] });
    },
  });
}

/** 회원 탈퇴(소프트 딜리트) Mutation */
export function useSignoutMutation() {
  return useMutation({
    mutationFn: () => userApi.signout(),
  });
}

/** 회원 데이터 영구 삭제 Mutation */
export function useHardDeleteMutation() {
  return useMutation({
    mutationFn: () => userApi.hardDelete(),
  });
}


// ==========================================
// 3. 자녀 관련 쿼리 & 뮤테이션 (Kids)
// ==========================================

export const kidsQueries = {
  all: ['kids'] as const,
  // 자녀 목록 조회
  list: (userId: number) =>
    queryOptions({
      queryKey: [...kidsQueries.all, 'list', userId] as const,
      queryFn: () => kidsApi.getKids(userId).then((res) => res.data),
    }),
  // 자녀 상세 조회
  detail: (kidId: number) =>
    queryOptions({
      queryKey: [...kidsQueries.all, 'detail', kidId] as const,
      queryFn: () => kidsApi.getKidDetail(kidId).then((res) => res.data),
      meta: { errorMessage: '리포트 로드에 실패했습니다. 다시 시도해주세요.' },
    }),
  // 자녀 분석 레포트 상세 조회
  reportDetail: (reportId: number) =>
    queryOptions({
      queryKey: [...kidsQueries.all, 'reportDetail', reportId] as const,
      queryFn: () => kidsApi.getKidReportDetail(reportId).then((res) => res.data),
      meta: { errorMessage: '리포트 상세 조회에 실패했습니다. 다시 시도해주세요.' },
    }),
  // 문답 영상 분석 상세 조회
  questionVideoDetail: (mediaId: number) =>
    queryOptions({
      queryKey: [...kidsQueries.all, 'questionVideoDetail', mediaId] as const,
      queryFn: () => kidsApi.getQuestionVideoDetail(mediaId).then((res) => res.data),
    }),
};

/** 자녀 목록 조회 Query */
export function useKidsQuery(userId: number, options?: { enabled?: boolean }) {
  return useQuery({
    ...kidsQueries.list(userId),
    ...options,
  });
}

/** 자녀 상세 정보 조회 Query */
export function useKidDetailQuery(kidId: number, options?: { enabled?: boolean }) {
  return useQuery({
    ...kidsQueries.detail(kidId),
    ...options,
  });
}

/** 자녀 분석 레포트 상세 조회 Query */
export function useKidReportDetailQuery(reportId: number, options?: { enabled?: boolean }) {
  return useQuery({
    ...kidsQueries.reportDetail(reportId),
    ...options,
  });
}

/** 문답 영상 분석 상세 조회 Query */
export function useQuestionVideoDetailQuery(mediaId: number, options?: { enabled?: boolean }) {
  return useQuery({
    ...kidsQueries.questionVideoDetail(mediaId),
    ...options,
  });
}

/** 자녀 정보 제거 Mutation */
export function useRemoveKidMutation(userId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (kidId: number) => kidsApi.removeKid(kidId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...kidsQueries.all, 'list', userId] });
    },
  });
}

/** 자녀 정보 수정 Mutation */
export function useEditKidMutation(kidId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateKidDto) => kidsApi.editKid(kidId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...kidsQueries.all, 'detail', kidId] });
    },
  });
}

/** 자녀 프로필 이미지 변경 Mutation */
export function useEditKidProfileImageMutation(kidId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (fd: FormData) => kidsApi.editKidProfileImage(kidId, fd),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...kidsQueries.all, 'detail', kidId] });
    },
  });
}


// ==========================================
// 4. 레포트 관련 쿼리 & 뮤테이션 (Report)
// ==========================================

export const reportQueries = {
  all: ['report'] as const,
  // 다중 레포트 지능 비교 데이터 조회
  compareList: (queryValue: any) =>
    queryOptions({
      queryKey: [...reportQueries.all, 'compareList', queryValue] as const,
      queryFn: () => reportApi.getReportsCompareData(queryValue).then((res) => res.data),
      meta: { errorMessage: '이전 비교 데이터 로드에 실패했습니다. 다시 시도해주세요.' },
    }),
  // 자녀 전체 기간 지능 비교 데이터 조회
  totalCompareList: (queryValue: any) =>
    queryOptions({
      queryKey: [...reportQueries.all, 'totalCompareList', queryValue] as const,
      queryFn: () => reportApi.getReportsTotalCompareData(queryValue).then((res) => res.data),
      meta: { errorMessage: '누적 비교 데이터 로드에 실패했습니다. 다시 시도해주세요.' },
    }),
  // 자녀의 모든 영상 리스트 조회
  allVideosList: (userId: number) =>
    queryOptions({
      queryKey: [...reportQueries.all, 'allVideosList', userId] as const,
      queryFn: () => reportApi.getAllVideos(userId).then((res) => res.data),
      meta: { errorMessage: '비디오를 불러오는 데 실패했습니다. 다시 시도해주세요.' },
    }),
  // 월별 분석 영상 리스트 조회
  videosByMonthList: (userId: number) =>
    queryOptions({
      queryKey: [...reportQueries.all, 'videosByMonthList', userId] as const,
      queryFn: () => reportApi.getVideosByMonth(userId).then((res) => res.data),
      meta: { errorMessage: '월별 영상을 불러오는 데 실패했습니다. 다시 시도해주세요.' },
    }),
  // 지능 영역별 분석 영상 리스트 조회
  videosByIntelligenceList: (userId: number, intelligence: string) =>
    queryOptions({
      queryKey: [...reportQueries.all, 'videosByIntelligenceList', userId, intelligence] as const,
      queryFn: () => reportApi.getVideosByIntelligence(userId, intelligence).then((res) => res.data),
      meta: { errorMessage: '지능 영상을 불러오는 데 실패했습니다. 다시 시도해주세요.' },
    }),
  // 즐겨찾기(북마크) 영상 리스트 조회
  favoriteVideosList: (userId: number) =>
    queryOptions({
      queryKey: [...reportQueries.all, 'favoriteVideosList', userId] as const,
      queryFn: () => reportApi.getFavoriteVideos(userId).then((res) => res.data),
      meta: { errorMessage: '즐겨찾기 영상을 불러오는 데 실패했습니다. 다시 시도해주세요.' },
    }),
  // 특정 영상의 강점/약점 코어 데이터 상세 조회
  intelligenceCoreDetail: (mediaId: number) =>
    queryOptions({
      queryKey: [...reportQueries.all, 'intelligenceCoreDetail', mediaId] as const,
      queryFn: () => reportApi.getIntelligenceCoreByVideo(mediaId).then((res) => res.data),
    }),
};

/** 다중 레포트 비교 데이터 조회 Query */
export function useReportsCompareDataQuery(queryValue: any, options?: { enabled?: boolean }) {
  return useQuery({
    ...reportQueries.compareList(queryValue),
    ...options,
  });
}

/** 자녀 전체 기간 지능 비교 데이터 조회 Query */
export function useReportsTotalCompareDataQuery(queryValue: any, options?: { enabled?: boolean }) {
  return useQuery({
    ...reportQueries.totalCompareList(queryValue),
    ...options,
  });
}

/** 자녀의 모든 영상 리스트 조회 Query */
export function useAllVideosQuery(userId: number, options?: { enabled?: boolean }) {
  return useQuery({
    ...reportQueries.allVideosList(userId),
    ...options,
  });
}

/** 월별 분석 영상 리스트 조회 Query */
export function useVideosByMonthQuery(userId: number, options?: { enabled?: boolean }) {
  return useQuery({
    ...reportQueries.videosByMonthList(userId),
    ...options,
  });
}

/** 특정 지능 영역별 영상 리스트 조회 Query */
export function useVideosByIntelligenceQuery(userId: number, intelligence: string, options?: { enabled?: boolean }) {
  return useQuery({
    ...reportQueries.videosByIntelligenceList(userId, intelligence),
    ...options,
  });
}

/** 즐겨찾기 영상 리스트 조회 Query */
export function useFavoriteVideosQuery(userId: number, options?: { enabled?: boolean }) {
  return useQuery({
    ...reportQueries.favoriteVideosList(userId),
    ...options,
  });
}

/** 특정 영상의 강점/약점 코어 상세 조회 Query */
export function useIntelligenceCoreByVideoQuery(mediaId: number, options?: { enabled?: boolean }) {
  return useQuery({
    ...reportQueries.intelligenceCoreDetail(mediaId),
    ...options,
  });
}

/** 영상 분석 요청 Mutation */
export function usePostThumbnailVideoSetMutation() {
  return useMutation({
    mutationFn: (fd: FormData) => reportApi.postThumbnailVideoSet(fd),
  });
}

/** 영상 분석 요청 (옵션 포함) Mutation */
export function usePostThumbnailVideoSet2Mutation() {
  return useMutation({
    mutationFn: ({ fd, option }: { fd: FormData; option: any }) => reportApi.postThumbnailVideoSet2(fd, option),
  });
}

/** 영상 즐겨찾기 등록/해제 Mutation */
export function useMakeFavoriteVideoMutation(userId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ mediaId, favorite }: { mediaId: number; favorite: boolean }) => reportApi.makeFavoriteVideo(mediaId, favorite),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...reportQueries.all, 'favoriteVideosList', userId] });
    },
  });
}

/** 업로드된 분석 원본 영상 삭제 Mutation */
export function useRemoveReportVideoMutation() {
  return useMutation({
    mutationFn: (url: string) => reportApi.removeReportVideo(url),
  });
}

/** 리포트 기록 전체 제거 Mutation */
export function useRemoveReportMutation() {
  return useMutation({
    mutationFn: (reportId: number) => reportApi.removeReport(reportId),
  });
}

/** 리포트 생성 완료(성공) 플래그 갱신 Mutation */
export function useMakeReportSuccessMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (reportId: number) => reportApi.makeReportSuccess(reportId),
    onSuccess: (res: any, reportId) => {
      if (!res?.data?.success) return;
      // 완료 처리한 리포트를 캐시에도 즉시 반영한다. 그래야 바로 이어지는 영상 분석이
      // 이 리포트를 "작업 중" 으로 보고 영상을 덧붙이지 않는다(user-177)
      queryClient.setQueriesData({ queryKey: [...kidsQueries.all, 'detail'] }, (old: any) =>
        old?.reports
          ? {
              ...old,
              reports: old.reports.map((r: any) =>
                r.id === reportId ? { ...r, isSuccess: true } : r
              ),
            }
          : old
      );
      queryClient.invalidateQueries({ queryKey: kidsQueries.all });
    },
  });
}


// ==========================================
// 5. AI 관련 쿼리 & 뮤테이션 (AI)
// ==========================================

/** 자녀 영상의 AI 썸네일 생성 Mutation */
export function useGetKidVideoThumbnailMutation() {
  return useMutation({
    mutationFn: (fd: FormData) => aiApi.getKidVideoThumbnail(fd),
  });
}


// ==========================================
// 6. 커뮤니티 관련 쿼리 & 뮤테이션 (Community)
// ==========================================

interface ComplaintReason {
  code: string;
  name: string;
}

export const communityQueries = {
  all: ['community'] as const,
  // 신고 사유 목록 조회
  complaintReasonsList: (open: boolean) =>
    queryOptions({
      queryKey: [...communityQueries.all, 'complaintReasonsList'] as const,
      queryFn: async () => {
        const res = await communityApi.getComplaintReasons();
        return res.data as ComplaintReason[];
      },
      enabled: open,
    }),
  // 공지사항 전체 목록 조회
  noticeList: () =>
    queryOptions({
      queryKey: [...communityQueries.all, 'noticeList'] as const,
      queryFn: () => communityApi.getNoticeList().then((res) => res.data),
    }),
  // 공지사항 키워드 검색 리스트 조회
  noticeListSearch: (queryValue: any) =>
    queryOptions({
      queryKey: [...communityQueries.all, 'noticeListSearch', queryValue] as const,
      queryFn: () => communityApi.getNoticeListSearch(queryValue).then((res) => res.data),
      meta: { errorMessage: '공지사항을 불러오는 데 실패했습니다. 다시 시도해주세요.' },
    }),
  // 이벤트 목록 조회
  eventList: (queryValue: any) =>
    queryOptions({
      queryKey: [...communityQueries.all, 'eventList', queryValue] as const,
      queryFn: () => communityApi.getEventList(queryValue).then((res) => res.data),
      meta: { errorMessage: '이벤트를 불러오는 데 실패했습니다. 다시 시도해주세요.' },
    }),
  // 이벤트 상세 조회
  eventDetail: (eventId: number) =>
    queryOptions({
      queryKey: [...communityQueries.all, 'eventDetail', eventId] as const,
      queryFn: () => communityApi.getEventDetail(eventId).then((res) => res.data),
      meta: { errorMessage: '이벤트를 불러오는 데 실패했습니다. 다시 시도해주세요.' },
    }),
  // 이벤트 댓글 목록 조회
  eventCommentsList: (queryValue: any) =>
    queryOptions({
      queryKey: [...communityQueries.all, 'eventCommentsList', queryValue] as const,
      queryFn: () => communityApi.getEventComments(queryValue).then((res) => res.data),
      meta: { errorMessage: '이벤트 댓글을 불러오는 데 실패했습니다. 다시 시도해주세요.' },
    }),
  // Q&A 질문 목록 조회
  qnaList: () =>
    queryOptions({
      queryKey: [...communityQueries.all, 'qnaList'] as const,
      queryFn: () => communityApi.getQnaList().then((res) => res.data),
    }),
  // Q&A 질문 키워드 검색 리스트 조회
  qnaListSearch: (queryValue: any) =>
    queryOptions({
      queryKey: [...communityQueries.all, 'qnaListSearch', queryValue] as const,
      queryFn: () => communityApi.getQnaListSearch(queryValue).then((res) => res.data),
    }),
  // Q&A 질문 키워드 검색 리스트 조회 (무한 스크롤용)
  qnaListSearchInfiniteList: (params: { limit: number; questionType: string; userType: string; order: string }) => {
    const { limit = 10, ...rest } = params;
    return infiniteQueryOptions({
      queryKey: [...communityQueries.all, 'qnaListSearchInfiniteList', rest] as const,
      queryFn: ({ pageParam }) =>
        communityApi.getQnaListSearch({
          page: pageParam as number,
          limit,
          ...rest,
        }).then((res) => res.data),
      initialPageParam: 1,
      getNextPageParam: (lastPage: any, allPages: any[]) => {
        const totalCount = lastPage.total;
        const loadedCount = allPages.length * limit;
        return totalCount > loadedCount ? allPages.length + 1 : undefined;
      },
      meta: { errorMessage: 'community.qna.toast_load_fail' },
    });
  },
  // Q&A 질문글 상세 조회
  qnaDetail: (postId: number, userId: number) =>
    queryOptions({
      queryKey: [...communityQueries.all, 'qnaDetail', postId, userId] as const,
      queryFn: () => communityApi.getQnaDetail(postId, userId).then((res) => res.data),
      meta: { errorMessage: '게시글과 답변을 불러오는 데 실패했습니다. 다시 시도해주세요.' },
    }),
  // 내가 작성한 Q&A 질문 상세 리스트 조회
  myQnaDetailList: (queryValue: any) =>
    queryOptions({
      queryKey: [...communityQueries.all, 'myQnaDetailList', queryValue] as const,
      queryFn: () => communityApi.getMyQnaDetail(queryValue).then((res) => res.data),
      meta: { errorMessage: '내 Q&A를 불러오는 데 실패했습니다. 다시 시도해주세요.' },
    }),
  // Q&A 답변 댓글 목록 조회
  qnaCommentsList: (queryValue: any) =>
    queryOptions({
      queryKey: [...communityQueries.all, 'qnaCommentsList', queryValue] as const,
      queryFn: () => communityApi.getQnaComments(queryValue).then((res) => res.data),
      meta: { errorMessage: '댓글을 불러오는 데 실패했습니다. 다시 시도해주세요.' },
    }),
  // Q&A 전문가 답변 내용 상세 조회
  expertAnswerDetail: (queryValue: any) =>
    queryOptions({
      queryKey: [...communityQueries.all, 'expertAnswerDetail', queryValue] as const,
      queryFn: () => communityApi.getExpertAnswer(queryValue).then((res) => res.data),
      meta: { errorMessage: '전문가 답변을 불러오는 데 실패했습니다. 다시 시도해주세요.' },
    }),
  // 자유게시판 글 목록 조회
  freePostList: (queryValue: any) =>
    queryOptions({
      queryKey: [...communityQueries.all, 'freePostList', queryValue] as const,
      queryFn: () => communityApi.getFreePostList(queryValue).then((res) => res.data),
    }),
  // 자유게시판 글 목록 조회 (무한 스크롤용)
  freePostInfiniteList: (params: { limit: number; sortBy: string }) => {
    const { limit = 10, ...rest } = params;
    return infiniteQueryOptions({
      queryKey: [...communityQueries.all, 'freePostInfiniteList', rest] as const,
      queryFn: ({ pageParam }) =>
        communityApi.getFreePostList({
          page: pageParam as number,
          limit,
          ...rest,
        }).then((res) => res.data),
      initialPageParam: 1,
      getNextPageParam: (lastPage: any, allPages: any[]) => {
        const totalCount = lastPage.total;
        const loadedCount = allPages.length * limit;
        return totalCount > loadedCount ? allPages.length + 1 : undefined;
      },
      meta: { errorMessage: 'community.freeboard.toast_load_fail' },
    });
  },
  // 자유게시판 글 상세 조회
  freePostDetail: (userId: number, postId: number) =>
    queryOptions({
      queryKey: [...communityQueries.all, 'freePostDetail', userId, postId] as const,
      queryFn: () => communityApi.getFreePostDetail(userId, postId).then((res) => res.data),
      meta: { errorMessage: 'community.freeboard.detail_toast_load_fail' },
    }),
  // 자유게시판 댓글 목록 조회
  freeCommentsList: (queryValue: any) =>
    queryOptions({
      queryKey: [...communityQueries.all, 'freeCommentsList', queryValue] as const,
      queryFn: () => communityApi.getFreeComments(queryValue).then((res) => res.data),
    }),
  // 자유게시판 댓글 목록 조회 (무한 스크롤용)
  freeCommentsInfiniteList: (params: { postId: number; limit: number; order: string; sortBy: string }) => {
    const { limit = 10, ...rest } = params;
    return infiniteQueryOptions({
      queryKey: [...communityQueries.all, 'freeCommentsInfiniteList', rest] as const,
      queryFn: ({ pageParam }) =>
        communityApi.getFreeComments({
          page: pageParam as number,
          limit,
          ...rest,
        }).then((res) => res.data),
      initialPageParam: 1,
      getNextPageParam: (lastPage: any, allPages: any[]) => {
        const totalCount = lastPage.total;
        const loadedCount = allPages.length * limit;
        return totalCount > loadedCount ? allPages.length + 1 : undefined;
      },
      meta: { errorMessage: 'community.toast_comment_load_fail' },
    });
  },
};

/** 신고 사유 목록 조회 Query */
export function useComplaintReasonsQuery(open: boolean) {
  return useQuery(communityQueries.complaintReasonsList(open));
}

/** 공지사항 목록 조회 Query */
export function useNoticeListQuery(options?: { enabled?: boolean }) {
  return useQuery({
    ...communityQueries.noticeList(),
    ...options,
  });
}

/** 공지사항 검색 리스트 조회 Query */
export function useNoticeListSearchQuery(queryValue: any, options?: { enabled?: boolean }) {
  return useQuery({
    ...communityQueries.noticeListSearch(queryValue),
    ...options,
  });
}

/** 이벤트 목록 조회 Query */
export function useEventListQuery(queryValue: any, options?: { enabled?: boolean }) {
  return useQuery({
    ...communityQueries.eventList(queryValue),
    ...options,
  });
}

/** 이벤트 상세 내용 조회 Query */
export function useEventDetailQuery(eventId: number, options?: { enabled?: boolean }) {
  return useQuery({
    ...communityQueries.eventDetail(eventId),
    ...options,
  });
}

/** 이벤트 댓글 목록 조회 Query */
export function useEventCommentsQuery(queryValue: any, options?: { enabled?: boolean }) {
  return useQuery({
    ...communityQueries.eventCommentsList(queryValue),
    ...options,
  });
}

/** Q&A 질문 목록 조회 Query */
export function useQnaListQuery(options?: { enabled?: boolean }) {
  return useQuery({
    ...communityQueries.qnaList(),
    ...options,
  });
}

/** Q&A 질문 검색 리스트 조회 Query */
export function useQnaListSearchQuery(queryValue: any, options?: { enabled?: boolean }) {
  return useQuery({
    ...communityQueries.qnaListSearch(queryValue),
    ...options,
  });
}

/** Q&A 질문 검색 리스트 조회 Query (무한 스크롤용) */
export function useQnaListSearchInfiniteQuery(params: { limit: number; questionType: string; userType: string; order: string }, options?: { enabled?: boolean }) {
  return useInfiniteQuery({
    ...communityQueries.qnaListSearchInfiniteList(params),
    ...options,
  });
}

/** Q&A 질문 상세 조회 Query */
export function useQnaDetailQuery(postId: number, userId: number, options?: { enabled?: boolean }) {
  return useQuery({
    ...communityQueries.qnaDetail(postId, userId),
    ...options,
  });
}

/** 내가 쓴 Q&A 상세 조회 Query */
export function useMyQnaDetailQuery(queryValue: any, options?: { enabled?: boolean }) {
  return useQuery({
    ...communityQueries.myQnaDetailList(queryValue),
    ...options,
  });
}

/** Q&A 답변 댓글 목록 조회 Query */
export function useQnaCommentsQuery(queryValue: any, options?: { enabled?: boolean }) {
  return useQuery({
    ...communityQueries.qnaCommentsList(queryValue),
    ...options,
  });
}

/** Q&A 전문가 답변 조회 Query */
export function useExpertAnswerQuery(queryValue: any, options?: { enabled?: boolean }) {
  return useQuery({
    ...communityQueries.expertAnswerDetail(queryValue),
    ...options,
  });
}

/** 자유게시판 글 목록 조회 Query */
export function useFreePostListQuery(queryValue: any, options?: { enabled?: boolean }) {
  return useQuery({
    ...communityQueries.freePostList(queryValue),
    ...options,
  });
}

/** 자유게시판 글 목록 조회 Query (무한 스크롤용) */
export function useFreePostInfiniteListQuery(params: { limit: number; sortBy: string }) {
  return useInfiniteQuery(communityQueries.freePostInfiniteList(params));
}

/** 자유게시판 상세 조회 Query */
export function useFreePostDetailQuery(userId: number, postId: number, options?: { enabled?: boolean }) {
  return useQuery({
    ...communityQueries.freePostDetail(userId, postId),
    ...options,
  });
}

/** 자유게시판 댓글 목록 조회 Query */
export function useFreeCommentsQuery(queryValue: any, options?: { enabled?: boolean }) {
  return useQuery({
    ...communityQueries.freeCommentsList(queryValue),
    ...options,
  });
}

/** 자유게시판 댓글 목록 조회 Query (무한 스크롤용) */
export function useFreeCommentsInfiniteListQuery(params: { postId: number; limit: number; order: string; sortBy: string }, options?: { enabled?: boolean }) {
  return useInfiniteQuery({
    ...communityQueries.freeCommentsInfiniteList(params),
    ...options,
  });
}

/** 게시글/댓글 신고 요청 Mutation */
export function usePostComplaintMutation() {
  return useMutation({
    mutationFn: (data: CreateComplaintDto) => communityApi.postComplaint(data),
  });
}

/** 이벤트 댓글 작성 Mutation */
export function useCreateEventCommentMutation(eventId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { content: string }) => communityApi.createEventComment(eventId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...communityQueries.all, 'eventCommentsList'] });
    },
  });
}

/** 이벤트 댓글 삭제 Mutation */
export function useDeleteEventCommentMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (commentId: number) => communityApi.deleteEventComment(commentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...communityQueries.all, 'eventCommentsList'] });
    },
  });
}

/** Q&A 질문글 등록 Mutation */
export function useCreateQnaMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => communityApi.createQna(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...communityQueries.all, 'qnaList'] });
    },
  });
}

/** Q&A 질문글 수정 Mutation */
export function useEditQnaMutation(postId: number, userId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => communityApi.editQna(postId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...communityQueries.all, 'qnaDetail', postId, userId] });
    },
  });
}

/** Q&A 답변 댓글 등록 Mutation */
export function useCreateCommentMutation(postId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { content: string }) => communityApi.createComment(postId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...communityQueries.all, 'qnaCommentsList'] });
    },
  });
}

/** Q&A 추천/좋아요 토글 Mutation */
export function useToggleLikeMutation(postId: number, userId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => communityApi.toggleLike(postId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...communityQueries.all, 'qnaDetail', postId, userId] });
    },
  });
}

/** Q&A 질문글 삭제 Mutation */
export function useDeletePostMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (postId: number) => communityApi.deletePost(postId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...communityQueries.all, 'qnaList'] });
    },
  });
}

/** Q&A 답변 댓글 삭제 Mutation */
export function useDeleteCommentMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (commentId: number) => communityApi.deleteComment(commentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...communityQueries.all, 'qnaCommentsList'] });
    },
  });
}

/** 자유게시판 글 등록 Mutation */
export function useCreateFreePostMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateFreeboardPostDto) => communityApi.createFreePost(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...communityQueries.all, 'freePostList'] });
    },
  });
}

/** 자유게시판 댓글 작성 Mutation */
export function useCreateFreePostCommentMutation(postId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { content: string }) => communityApi.createFreePostComment(postId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...communityQueries.all, 'freeCommentsList'] });
      queryClient.invalidateQueries({ queryKey: [...communityQueries.all, 'freeCommentsInfiniteList'] });
    },
  });
}

/** 자유게시판 글 좋아요 토글 Mutation */
export function useToggleFreePostLikeMutation(postId: number, userId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => communityApi.toggleFeePostLike(postId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...communityQueries.all, 'freePostDetail', userId, postId] });
    },
  });
}

/** 자유게시판 댓글 삭제 Mutation */
export function useDeleteFreeCommentMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (commentId: number) => communityApi.deleteFreeComment(commentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...communityQueries.all, 'freeCommentsList'] });
      queryClient.invalidateQueries({ queryKey: [...communityQueries.all, 'freeCommentsInfiniteList'] });
    },
  });
}


// ==========================================
// 7. 마이페이지(FAQ/1:1문의) 관련 쿼리 & 뮤테이션 (My)
// ==========================================

export const myQueries = {
  all: ['my'] as const,
  // 자주 묻는 질문(FAQ) 목록 조회
  frequentQnaList: () =>
    queryOptions({
      queryKey: [...myQueries.all, 'frequentQnaList'] as const,
      queryFn: () => myApi.getFrequentQnaList().then((res) => res.data),
    }),
  // 자주 묻는 질문 카테고리 목록 조회
  frequentQnaCategoryList: () =>
    queryOptions({
      queryKey: [...myQueries.all, 'frequentQnaCategoryList'] as const,
      queryFn: () => myApi.getFrequentQnaCategory().then((res) => res.data),
    }),
  // 자주 묻는 질문 내용 상세 조회
  frequentQnaDetail: (qnaId: number) =>
    queryOptions({
      queryKey: [...myQueries.all, 'frequentQnaDetail', qnaId] as const,
      queryFn: () => myApi.getFrequentQnaDetail(qnaId).then((res) => res.data),
    }),
  // 내 1:1 문의 전체 리스트 조회
  privateQnaList: (queryValue: any) =>
    queryOptions({
      queryKey: [...myQueries.all, 'privateQnaList', queryValue] as const,
      queryFn: () => myApi.getPrivateQnaList(queryValue).then((res) => res.data),
    }),
  // 내 1:1 문의 전체 리스트 조회 (무한 스크롤용)
  privateQnaInfiniteList: (params: { limit: number }) => {
    const { limit = 10 } = params;
    return infiniteQueryOptions({
      queryKey: [...myQueries.all, 'privateQnaInfiniteList'] as const,
      queryFn: ({ pageParam }) =>
        myApi.getPrivateQnaList({
          page: pageParam as number,
          limit,
        }).then((res) => res.data),
      initialPageParam: 1,
      getNextPageParam: (lastPage: any, allPages: any[]) => {
        const totalCount = lastPage.total;
        const loadedCount = allPages.length * limit;
        return totalCount > loadedCount ? allPages.length + 1 : undefined;
      },
      meta: { errorMessage: 'my.posts.toast_load_fail' },
    });
  },
  // 내 1:1 문의 상세 조회
  privateQnaDetail: (qnaId: number) =>
    queryOptions({
      queryKey: [...myQueries.all, 'privateQnaDetail', qnaId] as const,
      queryFn: () => myApi.getPrivateQnaDetail(qnaId).then((res) => res.data),
      meta: { errorMessage: '1:1 문의를 불러오는 데 실패했습니다. 다시 시도해주세요.' },
    }),
  // 내가 작성한 자유게시판 글 목록 조회
  myFreeboardList: (queryValue: any) =>
    queryOptions({
      queryKey: [...myQueries.all, 'myFreeboardList', queryValue] as const,
      queryFn: () => myApi.getMyFreeboardList(queryValue).then((res) => res.data),
    }),
  // 내가 작성한 자유게시판 글 상세 조회
  myFreeboardDetail: (freeboardId: number) =>
    queryOptions({
      queryKey: [...myQueries.all, 'myFreeboardDetail', freeboardId] as const,
      queryFn: () => myApi.getMyFreeboardDetail(freeboardId).then((res) => res.data),
    }),
};

/** 자주 묻는 질문 목록 조회 Query */
export function useFrequentQnaListQuery(options?: { enabled?: boolean }) {
  return useQuery({
    ...myQueries.frequentQnaList(),
    ...options,
  });
}

/** 자주 묻는 질문 카테고리 조회 Query */
export function useFrequentQnaCategoryQuery(options?: { enabled?: boolean }) {
  return useQuery({
    ...myQueries.frequentQnaCategoryList(),
    ...options,
  });
}

/** 자주 묻는 질문 상세 내용 조회 Query */
export function useFrequentQnaDetailQuery(qnaId: number, options?: { enabled?: boolean }) {
  return useQuery({
    ...myQueries.frequentQnaDetail(qnaId),
    ...options,
  });
}

/** 내 1:1 문의 목록 조회 Query */
export function usePrivateQnaListQuery(queryValue: any, options?: { enabled?: boolean }) {
  return useQuery({
    ...myQueries.privateQnaList(queryValue),
    ...options,
  });
}

/** 내 1:1 문의 목록 조회 Query (무한 스크롤용) */
export function usePrivateQnaInfiniteListQuery(params: { limit: number }, options?: { enabled?: boolean }) {
  return useInfiniteQuery({
    ...myQueries.privateQnaInfiniteList(params),
    ...options,
  });
}

/** 내 1:1 문의 상세 조회 Query */
export function usePrivateQnaDetailQuery(qnaId: number, options?: { enabled?: boolean }) {
  return useQuery({
    ...myQueries.privateQnaDetail(qnaId),
    ...options,
  });
}

/** 내가 쓴 자유게시판 글 목록 조회 Query */
export function useMyFreeboardListQuery(queryValue: any, options?: { enabled?: boolean }) {
  return useQuery({
    ...myQueries.myFreeboardList(queryValue),
    ...options,
  });
}

/** 내가 쓴 자유게시판 글 상세 조회 Query */
export function useMyFreeboardDetailQuery(freeboardId: number, options?: { enabled?: boolean }) {
  return useQuery({
    ...myQueries.myFreeboardDetail(freeboardId),
    ...options,
  });
}

/** 1:1 신규 문의 작성 Mutation */
export function usePostPrivateQnaMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => myApi.postPrivateQna(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...myQueries.all, 'privateQnaList'] });
      queryClient.invalidateQueries({ queryKey: [...myQueries.all, 'privateQnaInfiniteList'] });
    },
  });
}

/** 1:1 문의글 제거 Mutation */
export function useDeletePrivateQnaMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (qnaId: number) => myApi.deletePrivateQna(qnaId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...myQueries.all, 'privateQnaList'] });
      queryClient.invalidateQueries({ queryKey: [...myQueries.all, 'privateQnaInfiniteList'] });
    },
  });
}

/** 내가 쓴 자유게시판 글 삭제 Mutation */
export function useDeleteMyFreeboardMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (freeboardId: number) => myApi.deleteMyFreeboard(freeboardId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...myQueries.all, 'myFreeboardList'] });
    },
  });
}
